import axios, { AxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { FirebaseUser } from '../Types';
import { auth } from '../Lib/firebase';
import { getIdToken, getIdTokenResult } from 'firebase/auth';
import { getDeviceId, getDeviceSerial, getDeviceToken } from '../Utils/helpers';
import { version } from 'src/utils/version';
import { API_ENDPOINT } from '../Lib/config';

const Axios = axios.create({
    baseURL: `${API_ENDPOINT}/api/v4`,
});

const usingDeviceToken = !!getDeviceToken();

Axios.defaults.headers.common['Authorization'] = `Device ${getDeviceToken()}`;
Axios.defaults.headers.common['X-Snackpass-Client'] = '@snackpass/snacktv-web';
Axios.defaults.headers.common['X-Snackpass-Client-Version'] = version;
Axios.defaults.headers.common['X-Snackpass-Device-Serial'] =
    getDeviceSerial() ?? '';
Axios.defaults.headers.common['X-Snackpass-Esper-ID'] = getDeviceId() ?? '';

if (usingDeviceToken) {
    Axios.interceptors.request.use(config => {
        const freshToken = getDeviceToken();
        if (freshToken && config.headers) {
            config.headers.Authorization = `Device ${freshToken}`;
        }
        return config;
    });
}

function shouldRefreshToken(config: AxiosRequestConfig) {
    if (usingDeviceToken) return false;

    const canBecomeDate = (
        value: string | number | boolean | undefined
    ): value is string | number =>
        typeof value === 'string' || typeof value === 'number';

    const expiration = config.headers?.Expiration;
    const authorization = config.headers?.Authorization;
    const isExpired =
        canBecomeDate(expiration) && new Date(expiration) <= new Date();
    const _shouldRefreshToken = !authorization || !expiration || isExpired;
    return _shouldRefreshToken;
}

async function refreshToken(config: AxiosRequestConfig) {
    if (usingDeviceToken) return;

    if (!auth.currentUser) return;
    const tokenResult = await getIdTokenResult(auth.currentUser);

    if (tokenResult) {
        const { token, expirationTime } = tokenResult;
        const authHeader = `Bearer ${token}`;
        // This is needed so the request executing this code doesn't fail, subsequent requests
        // will use the defaults
        if (config.headers) config.headers.Authorization = authHeader;
        Axios.defaults.headers.common['Expiration'] = expirationTime;
        Axios.defaults.headers.common['Authorization'] = authHeader;
    }
}

Axios.interceptors.response.use(
    response => response,
    async error => {
        if (usingDeviceToken) {
            const status = error?.response?.status;
            if (status === 401 || status === 403) {
                Sentry.captureMessage('Device token auth failed', {
                    level: 'error',
                    extra: {
                        status,
                        url: error?.config?.url,
                    },
                });
            }
            return;
        }

        if (axios.isAxiosError(error)) {
            // refresh token and retry original request if request
            // failed due to token expiration
            const originalRequest = error.config as AxiosRequestConfig & {
                _retry?: boolean;
            };
            const _shouldRefreshToken =
                shouldRefreshToken(originalRequest) &&
                error.response?.status === 403 &&
                !originalRequest._retry;
            if (_shouldRefreshToken) {
                originalRequest._retry = true;
                await refreshToken(originalRequest);
                return Axios(originalRequest);
            }
        }

        return Promise.reject(error);
    }
);

export const setAuthHeader = async (user: FirebaseUser) => {
    if (usingDeviceToken) return;

    if (!getDeviceToken() && user) {
        const token = await getIdToken(user);
        return (Axios.defaults.headers.common[
            'Authorization'
        ] = `Bearer ${token}`);
    }
};

export const removeAxiosHeaders = () => {
    if (usingDeviceToken) return;

    delete Axios.defaults.headers.common['Expiration'];
    delete Axios.defaults.headers.common['Authorization'];
};

export default Axios;
